import { v4 as uuidv4 } from 'uuid';
import { getNewDate, getTimestampWithMs } from 'lib/DatetimeManager';
import { logRumError } from 'lib/DDRum';

import Env from './Env';
import MediaDeviceDetector from './MediaDeviceDetector';
import gameIds from '../activities/hub/data/GameIds';
import { aquaAxios, callAsyncWithRetry, getBookGameId, getReportingGameId } from './utilityFunctions';
import getLastInternetSpeed from './InternetSpeedTest';

const SEARCH_LIGHT_API_URL = `${Env.SEARCHLIGHT_URL}/v${Env.SEARCHLIGHT_API_VERSION}/event`;
const SOURCE_ID = 'AQUA';
const SOURCE_FIELD_TRIAL_ID = `${SOURCE_ID} Field Study`;

export const SEARCHLIGHT_EVENT_TYPE = {
  ADMIN_DEBUG_EVENT: 'ADMIN_DEBUG_EVENT', // user uses Admin tool
  ADMIN_ENABLED_ON_PROD: 'ADMIN_ENABLED_ON_PROD', // user enabled Admin on Prod env
  APP_LOADED: 'APP_LOADED', // The Aqua App is loaded by a user.
  BONUS_GAME_COMPLETE: 'BONUS_GAME_COMPLETE', // bonus minigame ends
  BONUS_GAME_START: 'BONUS_GAME_START', // bonus minigame starts (ie mystery round of unmask that)
  COMPLETE_ASSESSMENT: 'COMPLETE_ASSESSMENT',
  CURIOSO_SELECTION: 'CURIOSO_SELECTION', // user selects curioso in quest intro
  EASTER_EGG_FOUND: 'EASTER_EGG_FOUND', // A student discovers or unlocks an easter egg
  ERROR: 'ERROR',
  EARLY_EXIT: 'EARLY_EXIT', // A student exits a game before completing standard level
  EXIT_GAME: 'EXIT_GAME', // A student exits a game.
  EXIT_IPT: 'EXIT_IPT',
  GAME_COMPLETE: 'GAME_COMPLETE', // A game has bene completed with one or more levels.
  HELP: 'HELP', // The student requests assistance during app usage.
  INSTRUCTION: 'INSTRUCTION', // The student enters instruction during a game
  LOG_IN: 'LOG_IN', // user logs in
  LAUNCH_DEMO: 'LAUNCH_DEMO',
  LEVEL_COMPLETE: 'LEVEL_COMPLETE', // A level has been completed with 1 or more rounds.
  LOAD_GAME: 'LOAD_GAME', // A game is loaded by a student.
  LOG_BAD_QUEST_STATE: 'LOG_BAD_QUEST_STATE',
  LOG_OUT: 'LOG_OUT', // logging out of metagame
  MEASURE_COMPLETE: 'MEASURE_COMPLETE',
  MINIGAME_LAUNCHED: 'MINIGAME_LAUNCHED', // launching a minigame from the metagame
  MULTIPLE_SESSIONS: 'MULTIPLE_SESSIONS', // multiple sessions on the same account
  NAVIGATION: 'NAVIGATION', // user navigates to some page
  ONBOARDING_COMPLETE: 'ONBOARDING_COMPLETE',
  ONBOARDING_START: 'ONBOARDING_START',
  PLAY_EFFECT: 'PLAY_EFFECT',
  PRACTICE: 'PRACTICE', // The student enters practice during a game.
  PROFILE_REQUEST: 'PROFILE_REQUEST',
  QUEST_COMPLETE: 'QUEST_COMPLETE', // user completes quest
  QUEST_START: 'QUEST_START', // user starts quest
  QUEST_STEP_AUTOCOMPLETE: 'QUEST_STEP_AUTOCOMPLETE',
  QUEST_STEP_COMPLETE: 'QUEST_STEP_COMPLETE', // user completed quest step
  QUEST_STEP_FAILURE: 'QUEST_STEP_FAILURE', // user fails quest step
  QUEST_STEP_AUTOCOMPLETE_ON_THREE_FAILURES:
    'QUEST_STEP_AUTOCOMPLETE_ON_THREE_FAILURES',
  QUIT_DEMO: 'QUIT_DEMO',
  READ_ALOUD: 'READ_ALOUD', // A student triggers audible content delivery during gameplay.
  REDIRECT: 'REDIRECT', // user navigates using redirect popup
  REPEAT_ONBOARDING: 'REPEAT_ONBOARDING', // Help button on progress screen was clicked
  REPEAT_QUESTION: 'REPEAT_QUESTION', // student clicks on soundbot
  REPORT_BACK_ONLINE: 'BACK_ONLINE', // connection restored
  RESET_DEMO: 'RESET_DEMO',
  REWARD_EARNED: 'REWARD_EARNED', // user earns augmentation
  REWARD_SPENT: 'REWARD_SPENT', // user spends augmentation
  ROUND_COMPLETE: 'ROUND_COMPLETE', // A game round has completed.
  ROUND_START: 'ROUND_START', // A game round has started.
  RW_AUDIO_BUTTON: 'RW_AUDIO_BUTTON',
  RW_CLOSE: 'RW_CLOSE',
  RW_OPEN: 'RW_OPEN',
  SELECT_ACTIVITY: 'SELECT_ACTIVITY', // an activity/screen was selected to load
  SELECT_FIELD: 'SELECT_FIELD',
  SELECTION: 'SELECTION', // A student has provided a response to a question.
  SESSION_REFRESH: 'SESSION_REFRESH',
  SETTING: 'SETTING', // user settings changed
  SIDE_QUEST_END: 'SIDE_QUEST_END', // user starts side quest
  START_ASSESSMENT: 'START_ASSESSMENT', // student begins APT at BOY or MOY
  APP_LAUNCHED: 'APP_LAUNCHED',
  SIDE_QUEST_START: 'SIDE_QUEST_START', // user starts side quest
  SELECT_HUB_SCREEN: 'SELECT_HUB_SCREEN',

  // eReader only events
  PAGE_TURN: 'PAGE_TURN',
  START_READING_MODE: 'START_READING_MODE',
  EXIT_READING_MODE: 'EXIT_READING_MODE',
  START_ACTIVITY: 'START_ACTIVITY',
  EXIT_ACTIVITY: 'EXIT_ACTIVITY',
  OPEN_TOC: 'OPEN_TOC',
  CLOSE_TOC: 'CLOSE_TOC',
  NAV_TOC: 'NAV_TOC',
  START_CHAPTER: 'START_CHAPTER',
  EXIT_CHAPTER: 'END_CHAPTER',
  CHANGE_READER_SETTINGS: 'CHANGE_READER_SETTINGS',
};

const SEARCHLIGHT_EVENT_TYPE_VALUES = Object.keys(SEARCHLIGHT_EVENT_TYPE).map(
  key => SEARCHLIGHT_EVENT_TYPE[key],
);

export const QUESTION_TYPES = {
  ONBOARDING: 'onboarding',
  PRACTICE: 'practice',
  REVIEW: 'review',
  SELF_CORRECT: 'selfCorrect',
  STANDARD: 'standard',
};

export const DESTINATION_GROUP = {
  MAP: 'map',
  PLACEMENT_TOOL: 'placementTool',
  TRO_BENCHMARK: 'tro_benchmark',
  SKILL_SCAN: 'skillScan',
  HUB: 'hub',
  QUEST: 'quest',
  GAME: 'game',
  PRACTICE: 'practice',
  CHALLENGE_GAME: 'challengeGame',
  MTG: 'mtg',
  SIDE_QUEST_GAME: 'sideQuestGame',
  WORKSHOP: 'workshop',
  CURIOSO_HUB: 'curiosoHub',
  CURIOSO_SELECTION: 'curiosoSelection',
  EREADER: 'ereader',
  VOCAB: 'vocab',
  CLOSE_READING: 'closeReading',
  PROGRESS_SCREEN: 'progressScreen',
  QUEST_PROGRESS_SCREEN: 'questProgressScreen',
  FLUENCY: 'fluency',
};

export const NAVIGATION_ORIGIN = {
  MAP: 'map',
  LOGIN: 'login',
  QUEST: 'quest',
  GAME: 'game',
  PRACTICE: 'practice',
  CHALLENGE_POPUP: 'challengePopup',
  MTG_POPUP: 'mtgPopup',
  SIDE_QUEST_POPUP: 'sideQuestPopup',
  WORKSHOP: 'workshop',
  CURIOSO: 'curioso',
  CURIOSO_SELECTION: 'curiosoSelection',
  PROGRESS_SCREEN: 'progressScreen',
  EXTERNAL_APP: 'externalApp',
  REDIRECT: 'redirect',
  QUEST_JOURNAL: 'questJournal',
  PLACEMENT_TOOL: 'placementTool',
  SKILL_SCAN: 'skillScan',
  TRO_BENCHMARK: 'tro_benchmark',
  MEASURE: 'measure',
  QUEST_PROGRESS_SCREEN: 'questProgressScreen',
  DEEPLINK: 'deeplink',
};

export const NAVIGATION_TRIGGER_TYPE = {
  MANUAL: 'manual',
  AUTOMATIC: 'automatic',
};

export const DESTINATION_DESCRIPTIONS = {
  [DESTINATION_GROUP.PLACEMENT_TOOL]: 'automatic placement activity for AR K-5',
  [DESTINATION_GROUP.TRO_BENCHMARK]: 'benchmark assessment for AR 1-5',
  [DESTINATION_GROUP.HUB]: 'the activity hub for AR K-5',
  [DESTINATION_GROUP.MAP]:
    'a type of activity hub, the bookerton map for AR K-3',
  [DESTINATION_GROUP.WORKSHOP]:
    'a type of activity hub, workshop hub for AR 4-5',
  [DESTINATION_GROUP.QUEST]: 'quest narrative screen for AR K-3',
  [DESTINATION_GROUP.GAME]: 'skill game for AR K-5',
  [DESTINATION_GROUP.PRACTICE]:
    'gallery of all playable skill games available for practicing',
  [DESTINATION_GROUP.CHALLENGE_GAME]: 'skill game in challenge mode for AR K-5',
  [DESTINATION_GROUP.MTG]: 'monthly mind the gap maze popup for AR 1-5',
  [DESTINATION_GROUP.SIDE_QUEST_GAME]:
    'skill game in side quest mode for AR K-3',
  [DESTINATION_GROUP.CURIOSO_HUB]:
    'curioso powers and customization for AR K-3',
  [DESTINATION_GROUP.CURIOSO_SELECTION]: 'curioso selection screen for AR K-5',
  [DESTINATION_GROUP.EREADER]: 'integrated eReader for AR K-5',
  [DESTINATION_GROUP.VOCAB]: 'vocabulary app for AR 3-5',
  [DESTINATION_GROUP.CLOSE_READING]: 'close reading app for AR 4-5',
  [DESTINATION_GROUP.PROGRESS_SCREEN]:
    'skill game level selection and progress screen for AR K-5',
  [DESTINATION_GROUP.QUEST_PROGRESS_SCREEN]: 'quest progress screen for AR 3-5',
};

export const METRIC_TYPE = {
  COUNT: 'count',
  TIME: 'time(ms)',
};

const METRIC_LOGIN_SERVICE_FAILURE = 'amplify.AQUA.App.login.servicefailure';
const METRIC_LOGIN_FAILURE = 'amplify.AQUA.App.login.failure';
const METRIC_LOGIN_SUCCESS = 'amplify.AQUA.App.login.success';
const METRIC_LOGIN_UNDETERMINED = 'amplify.AQUA.App.login.undetermined';
const METRIC_LOGIN_GRADE_RESTRICTED = 'amplify.AQUA.App.login.gradeRestricted';
const METRIC_LOGIN_NO_ONBOARDING_GRADE = 'amplify.AQUA.App.login.noOnboardingGrade';
const METRIC_LOGIN_UNKNOWN_QR = 'amplify.AQUA.App.login.unknownQr';
const METRIC_LOGIN_NON_AMPLIFY_QR = 'amplify.AQUA.App.login.nonAmplifyQr';
export const METRIC_LOGIN_GET_USER_DATA_RETRY = 'amplify.AQUA.App.login.getUserDataRetry';
export const METRIC_LOGIN_GET_PLACEMENT_ORIGIN_RETRY = 'amplify.AQUA.App.login.getPlacementOriginRetry';

let showSearchlightLogs = false;

if (Env.isDevEnv) {
  showSearchlightLogs =
    showSearchlightLogs ||
    sessionStorage.getItem('showSearchlightLogs') === 'true';
}

/* eslint-disable class-methods-use-this */
class SearchlightDispatcher {
  constructor(sessionId) {
    this.sourceId = SOURCE_ID;
    this.apiVersion = `${Env.SEARCHLIGHT_API_VERSION}`;

    this.lastMessageDate = undefined;
    this.lastMessageTimestamp = '';

    this.sessionId = sessionId;
    this.gameSessionId = uuidv4();
    this.questSessionId = '';
    this.skillScanSessionId = null;
    this.skillScanSessionData = {};

    this.identity = {
      authProvider: '',
      grade: 'Unknown',
      group: '',
      organization: '',
      userId: 'None',
      userType: 'student',
      isTestUser: false,
      isSpecialContentLicense: false,
    };

    const UAParser = require('ua-parser-js'); // eslint-disable-line global-require
    const parser = new UAParser();
    parser.setUA(global.navigator.userAgent);
    this.profile = parser.getResult();

    this.devices = new MediaDeviceDetector();
    this.devices.checkDeviceSupport();
  }

  getSession(isUsePreviousRoundSessionId, product) {
    return {
      sessionId: this.sessionId,
      gameSessionId: this.gameSessionId,
      roundSessionId:
        !this.roundSessionId && isUsePreviousRoundSessionId
          ? this.previousRoundSessionId
          : this.roundSessionId,
      sessionState: 'active',
      questSessionId: this.questSessionId,
      skillScanSessionId: this.skillScanSessionId,
      product,
    };
  }

  // return just the sessionId
  getSessionId() {
    return this.sessionId;
  }

  getGameSessionId() {
    return this.gameSessionId;
  }

  setGameSessionId(id) {
    this.gameSessionId = id || uuidv4();
  }

  setQuestSessionId(id = '') {
    this.questSessionId = id;
  }

  setRoundSessionId(roundSessionId) {
    this.roundSessionId = roundSessionId || uuidv4();
  }

  setSkillScanSessionId(skillScanSessionId) {
    this.skillScanSessionId = skillScanSessionId ?? uuidv4();
  }

  setSkillScanSessionData(skillScanSessionData) {
    this.skillScanSessionData = skillScanSessionData;
  }

  setSkillScanStartTimestamp() {
    this.skillScanSessionData.roundStartTimestamp = this.lastMessageTimestamp;
  }

  setSkillScanCompleteTimestamp() {
    this.skillScanSessionData.roundCompleteTimestamp = this.lastMessageTimestamp;
  }

  setSourceId(isFieldTrial) {
    this.sourceId = isFieldTrial ? SOURCE_FIELD_TRIAL_ID : SOURCE_ID;
  }

  clearRoundSessionId() {
    // roundSessionId should be cleared with ROUND_COMPLETE but sometimes events after ROUND_COMPLETE need it
    // so stash the last roundSessionId to use in those cases
    this.previousRoundSessionId = this.roundSessionId;
    this.roundSessionId = undefined;
  }

  makeTracekey(gameId = gameIds.METAGAME, session = {}) {
    const {
      sessionId = '0',
      gameSessionId = '0',
      roundSessionId = '0',
    } = session;
    return [gameId, sessionId, gameSessionId, roundSessionId].join('.');
  }

  /**
   * getUiType - Do our best to figure out the type of input
   *             https://stackoverflow.com/questions/4817029/whats-the-best-way-to-detect-a-touch-screen-device-using-javascript/4819886#4819886
   * @return {String}  "touch" (touchscreen) or "k-b" (Keyboard) per spec
   */
  getUiType() {
    const UITYPE = {
      keyboard: 'k-b',
      touchscreen: 'touch',
    };

    if (
      'ontouchstart' in global.window || // works on most browsers
      global.navigator.maxTouchPoints // works on IE10/11 and Surface
    ) {
      return UITYPE.touchscreen;
    }
    return UITYPE.keyboard;
  }

  getSourceProfile() {
    const profile = {
      os: this.profile.os.name || '',
      osVersion: this.profile.os.version || '',
      device: this.profile.device.type || '',
      deviceVendor: this.profile.device.vendor || '',
      deviceVersion: this.profile.device.model || '',
      uiType: this.getUiType(),
      internetSpeed: getLastInternetSpeed(),
    };

    profile.browser = this.profile.browser.name || '';
    profile.browserVersion = this.profile.browser.version || '';
    profile.hasSpeakers = this.devices.hasSpeakers;
    profile.hasMicrophone = this.devices.hasMicrophone;
    profile.hasWebcam = this.devices.hasWebcam;

    return profile;
  }

  updateGameId(gameId, product, details, program) {
    if (gameId === gameIds.E_READER) {
      return getBookGameId(details?.bookId) || gameId;
    }

    return getReportingGameId(gameId, product, program);
  }

  getApplication(application = {}, product, program) {
    const {
      gameId = gameIds.METAGAME,
      details: { devDetails, isIntermediateSave, ...details } = {}, // remove devDetails, isIntermediateSave (AQUA-38560) keys  from details object
      eventType,
      writtenByAPI,
    } = application;
    // warn about invalid Searchlight Event Types in testing environments
    if (Env.isDevEnv && !SEARCHLIGHT_EVENT_TYPE_VALUES.includes(eventType)) {
      console.warn(`${eventType} is not a valid Searchlight Event Type`);
    }
    return {
      gameId: this.updateGameId(gameId, product, details, program),
      eventType,
      writtenByAPI,
      details,
    };
  }

  changeGamePhase(gamePhase) {
    this.gamePhase = gamePhase;
  }

  setUserId(userId) {
    this.identity.userId = userId;
  }

  setIdentity({
    studentId,
    organization,
    group,
    grade,
    isTestUser,
    isStaffLogin,
    language = 'en',
    isDeepLink,
    deepLinkProgram,
  }) {
    this.identity = {
      ...this.identity,
      authProvider: '',
      userType: isStaffLogin ? 'teacher' : 'student',
      userId: studentId,
      group,
      organization,
      /* grade may be undefined when AR is opened from TD in Demo Mode */
      grade:
        grade === 'null' || grade === 'undefined' || grade === undefined
          ? '12'
          : grade,
      isTestUser,
      language,
      ...(isDeepLink && { isDeepLink }),
      ...(deepLinkProgram && { deepLinkProgram }),
    };
  }

  setIdentityLanguage(language = 'en') {
    this.identity.language = language;
  }

  setSkillMap(skillMap) {
    this.identity.skillMap = skillMap;
  }

  getElapsed(date = getNewDate()) {
    const elapsed = !this.lastMessageDate
      ? 0
      : date.getTime() - this.lastMessageDate.getTime();
    this.lastMessageDate = date;
    this.lastMessageTimestamp = getTimestampWithMs(this.lastMessageDate);
    return elapsed;
  }

  getMessage(fullConfig = {}) {
    // grab stuff we need from fullConfig but do not want to send. Gather everyhing else in 'config' var
    const {
      isUsePreviousRoundSessionId = false,
      session: { product, program },
      identity,
      skipApplication = false,
      ...config
    } = fullConfig;
    const { application = {}, log, metric } = config;

    // get current datetime, calc time elapsed since lastMessageDate and update lastMessageDate
    const date = getNewDate();
    const elapsed = this.getElapsed(date);

    const session = this.getSession(isUsePreviousRoundSessionId, product);

    return {
      ...config,
      elapsed,
      metric,
      session,
      application: skipApplication ? undefined : this.getApplication(application, product, program),
      log: !log
        ? undefined
        : {
          tracekey: this.makeTracekey(application.gameId, session),
          ...log,
        },
      timestamp: getTimestampWithMs(date.getTime()),
      source: this.sourceId,
      sourceCommitId: Env.SOURCE_COMMIT_ID,
      eventApiVersion: Env.SEARCHLIGHT_SCHEMA_VERSION,
      identity: {
        ...this.identity,
        ...identity,
      },
      sourceProfile: this.getSourceProfile(),
      validate: true,
    };
  }

  /**
   * Get the identity object for the Searchlight event,
   * extracted from the login response.
   * @param data the data from the login response
   * @returns the identity object
   */
  getIdentity = (
    {
      UserId = 'None',
      Groups,
      Organization,
      Grade = 'Unknown',
      IsTestUser = false,
      IsSpecialContentLicense = false,
      userType,
    },
  ) => ({
    authProvider: '',
    userId: `${UserId}`, // convert to string as Searchlight expects
    userType,
    group: Groups?.toString() ?? '',
    organization: Organization || '',
    // convert to boolean as Searchlight expects
    isTestUser: typeof IsTestUser === 'string'
      ? IsTestUser.toLowerCase() === 'true'
      : IsTestUser,
    isSpecialContentLicense: typeof IsSpecialContentLicense === 'string'
      ? IsSpecialContentLicense.toLowerCase() === 'true'
      : IsSpecialContentLicense,
    grade: Grade,
  });

  /**
   * Get the metric element of the Searchlight event.
   * @param {string} status the status of the login
   * @returns {object} { type: string, id: string, value: number }
   */
  getMetric = (status) => {
    const metric = { type: METRIC_TYPE.COUNT, value: 1 };
    switch (status) {
      case 'success':
        metric.id = METRIC_LOGIN_SUCCESS;
        break;
      case 'unlicensed':
        metric.id = METRIC_LOGIN_FAILURE;
        break;
      case 'failure':
        metric.id = METRIC_LOGIN_FAILURE;
        break;
      case 'unavailable':
        metric.id = METRIC_LOGIN_SERVICE_FAILURE;
        break;
      case 'unlicensed_grade_restricted':
        metric.id = METRIC_LOGIN_GRADE_RESTRICTED;
        break;
      case 'no_onboarding_grade':
        metric.id = METRIC_LOGIN_NO_ONBOARDING_GRADE;
        break;
      case 'unknown_qr':
        metric.id = METRIC_LOGIN_UNKNOWN_QR;
        break;
      case 'non_amplify_qr':
        metric.id = METRIC_LOGIN_NON_AMPLIFY_QR;
        break;
      default:
        metric.id = METRIC_LOGIN_UNDETERMINED;
    }
    return metric;
  };

  /**
   * Build the Searchlight error event.
   * @param {object} application contains the error information for Searchlight
   * @param {object} metric contains the metrics Searchlight
   * @param {object} log contains the logging information for Searchlight
   * @returns Searchlight message
   */
  buildErrorEvent = (application, metric, log) => ({
    eventApiVersion: Env.SEARCHLIGHT_SCHEMA_VERSION,
    source: this.sourceId,
    session: this.getSession(false, 'NA'),
    timestamp: getTimestampWithMs(),
    elapsed: this.getElapsed(),
    sourceProfile: this.getSourceProfile(),
    identity: this.getIdentity({ userType: 'other' }),
    application,
    metric,
    log,
  });

  /**
   * Build the Searchlight LOG_IN event.
   * @see https://docs.google.com/document/d/1vs3glqXAjVfqDuJ6QjMlRFXKuCqDf3cZv4wBEoo6RWk/edit#heading=h.4y93gom11wjf
   *      for acceptable values of loginType and status.
   * @param {string} status the status of the login
   * @param {object} data the login response data
   * @returns {object} the Searchlight message
   */
  buildLoginEvent = (status, data = {}) => ({
    eventApiVersion: Env.SEARCHLIGHT_SCHEMA_VERSION,
    source: this.sourceId,
    session: this.getSession(false, 'NA'),
    timestamp: getTimestampWithMs(),
    elapsed: this.getElapsed(),
    sourceProfile: this.getSourceProfile(),
    identity: this.getIdentity({ ...data, userType: data.userType ?? 'other' }),
    application: {
      gameId: gameIds.METAGAME,
      details: { loginType: 'login', status },
      eventType: SEARCHLIGHT_EVENT_TYPE.LOG_IN,
    },
    metric: this.getMetric(status),
    validate: true,
  });

  /**
   * Builds and dispatches the logging event for errors.
   */
  reportErrorLog = async (error, data, category = 'javascript') => {
    const errorApp = {
      gameId: gameIds.METAGAME,
      eventType: SEARCHLIGHT_EVENT_TYPE.ERROR,
      details: {
        error: error.toString(),
        data,
        category,
      },
    };
    const errorMetric = {
      type: 'count',
      id: 'amplify.AQUA.App.error.javascript',
      value: 1,
    };
    const errorLog = {
      level: 'error',
      logType: 'communication',
      message: error.toString(),
    };
    const searchlightEvent = this.buildErrorEvent(errorApp, errorMetric, errorLog);
    await this.dispatchEvent(searchlightEvent);
  };

  /**
 * @param status the status of the login event (success|failure)
 * @param data the login response data
 */
  reportLoginEvent = async (status, data = {}) => {
    try {
      const searchlightEvent = this.buildLoginEvent(status, data);
      await this.dispatchEvent(searchlightEvent);
    } catch (error) {
      console.warn('reportLoginEvent failed:', error);
    }
  };

  reportMetric = async (metricId, metricType, value, product) => {
    const event = {
      eventApiVersion: Env.SEARCHLIGHT_SCHEMA_VERSION,
      source: SOURCE_ID,
      session: this.getSession('none', product),
      timestamp: getTimestampWithMs(),
      elapsed: this.getElapsed(),
      sourceProfile: this.getSourceProfile(),
      identity: this.getIdentity({ userType: 'other' }),
      metric: {
        id: metricId,
        type: metricType,
        value,
      },
      validate: true,
      skipApplication: true,
    };

    await this.dispatchEvent(event);
  };

  // Example config for reporting a selection
  /* {
    eventType: SEARCHLIGHT_EVENT_TYPE.SELECTION, // Needs to be provided from the SEARCHLIGHT_EVENT_TYPE enum
    // Can include any data desired
    details: {
      level: 1,
      questionIndex: 1,
      questionType: standard,
      questionId: 34,
      questionGroup: "noun-pronoun",
      action: "cat",
      possibleActions: ["cat", "dog", "mouse"],
      targetAction: "dog",
    }
  } */
  async dispatchEvent(config) {
    const data = this.getMessage(config);
    if (Env.isDevEnv && showSearchlightLogs && data.application.eventType) {
      console.log(`searchlightEvent: ${data.application.eventType}\n`, data);
    }

    return callAsyncWithRetry(
      () => aquaAxios(SEARCH_LIGHT_API_URL, { method: 'POST', data }),
      {
        logger: (error, { timesAttempted }) => {
          if (timesAttempted === 3 && error?.response?.status === 400) {
            logRumError('Searchlight event request failed, 400', {
              eventType: data?.application?.eventType,
              detailFields: Object.keys(data?.application?.details || {}),
            });
          }
          console.warn(`Attempt ${timesAttempted}: ${data?.application?.eventType} failed with error:`, error.message);
        },
      },
    );
  }
}
/* eslint-enable class-methods-use-this */

export default SearchlightDispatcher;
